import { useState } from "react";
import { useDemo } from "../context/DemoContext";
import { formatCurrencyAmount, DEMO_EXCHANGE_RATES } from "../data/mock";
import type { Quote } from "../types";

export default function CorporateAccount() {
  const {
    demo,
    getDemoInternalAccounts,
    getDemoExternalAccounts,
    getDemoTransactions,
    getDemoCustomers,
    demoCreateQuote,
    demoExecuteQuote,
    demoTransferOut,
    demoTransferIn,
  } = useDemo();

  const platformIA = getDemoInternalAccounts("PLATFORM");
  const platformEA = getDemoExternalAccounts("PLATFORM");
  const allEA = getDemoExternalAccounts();
  const customers = getDemoCustomers();

  // Transfer form state
  const [showTransfer, setShowTransfer] = useState(false);
  type TransferMode = "transfer_out" | "transfer_in" | "quote";
  const [transferMode, setTransferMode] = useState<TransferMode>("transfer_out");

  // Common fields
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [destAccountId, setDestAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [transferResult, setTransferResult] = useState("");
  const [error, setError] = useState("");

  // Quote-specific
  const [pendingQuote, setPendingQuote] = useState<Quote | null>(null);

  const resetForm = () => {
    setSourceAccountId("");
    setDestAccountId("");
    setAmount("");
    setError("");
    setTransferResult("");
    setPendingQuote(null);
  };

  // Get source account for display
  const sourceAcct = platformIA.find((a) => a.id === sourceAccountId);

  // Filter external accounts based on mode
  const getDestExternalAccounts = () => {
    if (transferMode === "transfer_out") {
      // Same currency only - filter to matching currency
      if (!sourceAcct) return allEA;
      return allEA.filter((ea) => ea.currency.code === sourceAcct.balance.currency.code);
    }
    // For quotes, show all external accounts
    return allEA;
  };

  // For transfer-in: source is external, dest is platform internal
  const getSourceExternalAccounts = () => {
    if (!destAccountId) return platformEA;
    const destAcct = platformIA.find((a) => a.id === destAccountId);
    if (!destAcct) return platformEA;
    // Same currency only
    return platformEA.filter((ea) => ea.currency.code === destAcct.balance.currency.code);
  };

  // ── Transfer Out: Platform Internal → External (same currency) ──
  const handleTransferOut = () => {
    setError("");
    setTransferResult("");

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setError("Enter a valid positive amount");
      return;
    }

    const result = demoTransferOut({
      sourceInternalAccountId: sourceAccountId,
      destExternalAccountId: destAccountId,
      amount: parsed,
      description: `Platform payout`,
    });

    if (result.success && result.transaction) {
      setTransferResult(
        `Transfer out complete! ${formatCurrencyAmount(result.transaction.sentAmount)} sent. Tx: ${result.transaction.id}`,
      );
      resetForm();
    } else {
      setError(result.error || "Transfer failed");
    }
  };

  // ── Transfer In: External → Platform Internal (same currency) ──
  const handleTransferIn = () => {
    setError("");
    setTransferResult("");

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setError("Enter a valid positive amount");
      return;
    }

    const result = demoTransferIn({
      sourceExternalAccountId: sourceAccountId,
      destInternalAccountId: destAccountId,
      amount: parsed,
      description: `Platform deposit`,
    });

    if (result.success && result.transaction) {
      setTransferResult(
        `Transfer in complete! ${formatCurrencyAmount(result.transaction.receivedAmount)} received. Tx: ${result.transaction.id}`,
      );
      resetForm();
    } else {
      setError(result.error || "Transfer failed");
    }
  };

  // ── Quote: Platform Internal → External (cross-currency) ──
  const handleCreateQuote = () => {
    setError("");
    setTransferResult("");

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setError("Enter a valid positive amount");
      return;
    }

    const destEA = allEA.find((a) => a.id === destAccountId);
    if (!destEA) {
      setError("Select a destination external account");
      return;
    }

    const result = demoCreateQuote({
      sourceAccountId,
      destinationAccountId: destAccountId,
      destinationCurrency: destEA.currency.code,
      amount: parsed,
      lockedSide: "SENDING",
      description: `Platform quote to ${destEA.accountLabel}`,
    });

    if (result.success && result.quote) {
      setPendingQuote(result.quote);
    } else {
      setError(result.error || "Quote creation failed");
    }
  };

  const handleExecuteQuote = () => {
    if (!pendingQuote) return;
    setError("");

    const result = demoExecuteQuote(pendingQuote.quoteId);
    if (result.success && result.transaction) {
      setTransferResult(
        `Quote executed! ${formatCurrencyAmount(result.transaction.sentAmount)} → ${formatCurrencyAmount(result.transaction.receivedAmount)}. Tx: ${result.transaction.id}`,
      );
      resetForm();
    } else {
      setError(result.error || "Execution failed");
    }
  };

  // Total platform value (USD equivalent)
  const totalValueUSD = platformIA.reduce((sum, a) => {
    const code = a.balance.currency.code;
    const amt = a.balance.amount / Math.pow(10, a.balance.currency.decimals);
    if (code === "USD" || code === "USDC") return sum + amt;
    const key = `${code}:USD`;
    const rate = DEMO_EXCHANGE_RATES[key];
    if (rate) return sum + amt * rate;
    return sum + amt;
  }, 0);

  if (!demo.enabled) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight mb-4">
          Corporate Account
        </h1>
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-6 max-w-xl">
          <div className="text-sm text-yellow-400 font-medium mb-2">Demo Mode Required</div>
          <p className="text-sm text-[#8A8A8A]">
            Enable Demo Mode from the sidebar toggle to view corporate/platform accounts with mock data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
            Corporate Account
          </h1>
          <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded">
            Platform Treasury
          </span>
        </div>
        <p className="text-sm text-[#8A8A8A] mt-1">
          Lightspark Partner corporate accounts and treasury management
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
          <div className="text-xs text-[#8A8A8A] mb-1">Total Value (USD est.)</div>
          <div className="text-2xl font-semibold text-[#F8F8F7] font-mono">
            ${totalValueUSD.toLocaleString("en-US", { maximumFractionDigits: 0 })}
          </div>
        </div>
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
          <div className="text-xs text-[#8A8A8A] mb-1">Internal Accounts</div>
          <div className="text-2xl font-semibold text-[#F8F8F7]">{platformIA.length}</div>
          <div className="text-xs text-[#666666] mt-1">{platformIA.map((a) => a.balance.currency.code).join(", ")}</div>
        </div>
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
          <div className="text-xs text-[#8A8A8A] mb-1">External Accounts</div>
          <div className="text-2xl font-semibold text-[#F8F8F7]">{platformEA.length}</div>
          <div className="text-xs text-[#666666] mt-1">Bank + Crypto</div>
        </div>
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
          <div className="text-xs text-[#8A8A8A] mb-1">Managed Customers</div>
          <div className="text-2xl font-semibold text-[#F8F8F7]">{customers.length}</div>
          <div className="text-xs text-[#666666] mt-1">across all currencies</div>
        </div>
      </div>

      {/* Internal Accounts */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-semibold text-[#F8F8F7]">Platform Treasury Accounts</h2>
            <p className="text-[11px] text-[#666666] mt-0.5">Shared platform-level accounts for liquidity management and cross-currency settlement. Not tied to any individual customer.</p>
          </div>
          <button
            onClick={() => {
              setShowTransfer(!showTransfer);
              if (showTransfer) resetForm();
            }}
            className="text-xs bg-[#00B3E0] text-black font-semibold px-4 py-2 rounded-lg hover:bg-[#00C4F5]"
          >
            {showTransfer ? "Hide Transfer" : "New Transfer"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformIA.map((acct) => (
            <div key={acct.id} className="bg-[#111111] border border-indigo-500/20 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium text-[#F8F8F7]">
                  {acct.balance.currency.code} Account
                </span>
                <span className="text-[10px] text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">
                  Platform
                </span>
              </div>
              <div className="text-xl font-semibold font-mono text-[#F8F8F7] mb-2">
                {formatCurrencyAmount(acct.balance)}
              </div>
              <div className="text-[11px] text-[#666666] font-mono truncate">{acct.id}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Transfer Form */}
      {showTransfer && (
        <div className="bg-[#111111] border border-[#00B3E0]/20 rounded-lg p-6 mb-8 max-w-2xl">
          <h3 className="text-sm font-semibold text-[#F8F8F7] mb-4">Platform Transfer</h3>

          {/* Transfer Mode Tabs */}
          <div className="flex gap-1 mb-5 bg-[#0A0A0A] p-1 rounded-lg">
            {[
              { key: "transfer_out" as const, label: "Transfer Out", api: "POST /transfer-out" },
              { key: "transfer_in" as const, label: "Transfer In", api: "POST /transfer-in" },
              { key: "quote" as const, label: "Cross-Currency Quote", api: "POST /quotes" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setTransferMode(tab.key);
                  resetForm();
                }}
                className={`flex-1 text-xs py-2 px-3 rounded-md transition-colors ${
                  transferMode === tab.key
                    ? "bg-[#1A1A1A] text-[#F8F8F7] border border-[#333333]"
                    : "text-[#666666] hover:text-[#999999]"
                }`}
              >
                <div>{tab.label}</div>
                <div className="text-[10px] font-mono text-[#555555] mt-0.5">{tab.api}</div>
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}
          {transferResult && (
            <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-2 rounded text-sm">
              {transferResult}
            </div>
          )}

          {/* Pending Quote Review */}
          {pendingQuote && (
            <div className="mb-4 bg-[#0A0A0A] border border-[#00B3E0]/30 rounded-lg p-4">
              <div className="text-xs text-[#00B3E0] font-medium mb-3">Quote Ready - Review & Execute</div>
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <div className="text-[#8A8A8A] text-xs">Sending</div>
                  <div className="text-[#F8F8F7] font-mono">
                    {formatCurrencyAmount({ amount: pendingQuote.totalSendingAmount, currency: pendingQuote.sendingCurrency })}
                  </div>
                </div>
                <div>
                  <div className="text-[#8A8A8A] text-xs">Receiving</div>
                  <div className="text-[#F8F8F7] font-mono">
                    {formatCurrencyAmount({ amount: pendingQuote.totalReceivingAmount, currency: pendingQuote.receivingCurrency })}
                  </div>
                </div>
                <div>
                  <div className="text-[#8A8A8A] text-xs">Rate</div>
                  <div className="text-[#F8F8F7] font-mono text-xs">
                    1 {pendingQuote.sendingCurrency.code} = {pendingQuote.exchangeRate.toFixed(4)} {pendingQuote.receivingCurrency.code}
                  </div>
                </div>
                <div>
                  <div className="text-[#8A8A8A] text-xs">Fees</div>
                  <div className="text-[#F8F8F7] font-mono text-xs">
                    {formatCurrencyAmount({ amount: pendingQuote.feesIncluded, currency: pendingQuote.sendingCurrency })}
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-[#666666] mb-3">
                Expires: {new Date(pendingQuote.expiresAt).toLocaleTimeString()} | ID: {pendingQuote.quoteId}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleExecuteQuote}
                  className="flex-1 bg-[#00B3E0] text-black font-semibold py-2 rounded-lg hover:bg-[#00C4F5] text-sm"
                >
                  Execute Quote
                </button>
                <button
                  onClick={() => setPendingQuote(null)}
                  className="px-4 py-2 border border-[#333333] text-[#999999] rounded-lg hover:text-[#F8F8F7] text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!pendingQuote && (
            <div className="space-y-4">
              {/* Transfer Out: source = platform internal, dest = external */}
              {transferMode === "transfer_out" && (
                <>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Source Internal Account (Platform)
                      <span className="text-[#555555] ml-1 font-mono">source</span>
                    </label>
                    <select
                      value={sourceAccountId}
                      onChange={(e) => {
                        setSourceAccountId(e.target.value);
                        setDestAccountId("");
                      }}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select source...</option>
                      {platformIA.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.balance.currency.code}: {formatCurrencyAmount(a.balance)} ({a.id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Destination External Account
                      <span className="text-[#555555] ml-1 font-mono">destination</span>
                    </label>
                    <select
                      value={destAccountId}
                      onChange={(e) => setDestAccountId(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select destination...</option>
                      {getDestExternalAccounts().map((ea) => (
                        <option key={ea.id} value={ea.id}>
                          {ea.currency.code} - {ea.accountLabel} ({ea.accountType.replace(/_/g, " ")})
                        </option>
                      ))}
                    </select>
                    {sourceAcct && (
                      <div className="text-[10px] text-[#555555] mt-1">
                        Same-currency only: showing {sourceAcct.balance.currency.code} accounts
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Transfer In: source = external, dest = platform internal */}
              {transferMode === "transfer_in" && (
                <>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Destination Internal Account (Platform)
                      <span className="text-[#555555] ml-1 font-mono">destination</span>
                    </label>
                    <select
                      value={destAccountId}
                      onChange={(e) => {
                        setDestAccountId(e.target.value);
                        setSourceAccountId("");
                      }}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select destination...</option>
                      {platformIA.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.balance.currency.code}: {formatCurrencyAmount(a.balance)} ({a.id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Source External Account
                      <span className="text-[#555555] ml-1 font-mono">source</span>
                    </label>
                    <select
                      value={sourceAccountId}
                      onChange={(e) => setSourceAccountId(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select source...</option>
                      {getSourceExternalAccounts().map((ea) => (
                        <option key={ea.id} value={ea.id}>
                          {ea.currency.code} - {ea.accountLabel} ({ea.accountType.replace(/_/g, " ")})
                        </option>
                      ))}
                    </select>
                    {destAccountId && (
                      <div className="text-[10px] text-[#555555] mt-1">
                        Same-currency only: showing {platformIA.find((a) => a.id === destAccountId)?.balance.currency.code || ""} accounts
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Quote: source = platform internal, dest = any external */}
              {transferMode === "quote" && (
                <>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Source Internal Account (Platform)
                      <span className="text-[#555555] ml-1 font-mono">source.sourceType: "ACCOUNT"</span>
                    </label>
                    <select
                      value={sourceAccountId}
                      onChange={(e) => setSourceAccountId(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select source...</option>
                      {platformIA.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.balance.currency.code}: {formatCurrencyAmount(a.balance)} ({a.id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#8A8A8A] mb-1">
                      Destination External Account
                      <span className="text-[#555555] ml-1 font-mono">destination.destinationType: "ACCOUNT"</span>
                    </label>
                    <select
                      value={destAccountId}
                      onChange={(e) => setDestAccountId(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                    >
                      <option value="">Select destination...</option>
                      {allEA.map((ea) => (
                        <option key={ea.id} value={ea.id}>
                          {ea.currency.code} - {ea.accountLabel} ({ea.accountType.replace(/_/g, " ")})
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Show estimated rate */}
                  {sourceAcct && destAccountId && (() => {
                    const destEA = allEA.find((a) => a.id === destAccountId);
                    if (!destEA) return null;
                    const srcCode = sourceAcct.balance.currency.code;
                    const destCode = destEA.currency.code;
                    if (srcCode === destCode) return (
                      <div className="text-xs text-yellow-400/80 bg-yellow-500/5 border border-yellow-500/20 rounded px-3 py-2">
                        Same currency ({srcCode}). Consider using Transfer Out instead for lower fees.
                      </div>
                    );
                    const key = `${srcCode}:${destCode}`;
                    const rate = DEMO_EXCHANGE_RATES[key];
                    return rate ? (
                      <div className="text-xs text-[#8A8A8A] bg-[#0A0A0A] rounded px-3 py-2">
                        Est. rate: 1 {srcCode} = {rate.toFixed(4)} {destCode}
                      </div>
                    ) : null;
                  })()}
                </>
              )}

              <div>
                <label className="block text-xs text-[#8A8A8A] mb-1">Amount (major units)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="1000.00"
                  className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
                />
              </div>

              <button
                onClick={
                  transferMode === "transfer_out"
                    ? handleTransferOut
                    : transferMode === "transfer_in"
                      ? handleTransferIn
                      : handleCreateQuote
                }
                disabled={!sourceAccountId || !destAccountId || !amount}
                className="w-full bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] transition-colors disabled:opacity-50"
              >
                {transferMode === "quote" ? "Create Quote" : "Execute Transfer"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* External Accounts */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-[#F8F8F7] mb-4">External Accounts (Linked)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformEA.map((acct) => (
            <div key={acct.id} className="bg-[#111111] border border-[#222222] rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#F8F8F7]">
                  {acct.accountType.replace(/_/g, " ")}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  acct.status === "ACTIVE"
                    ? "bg-emerald-500/10 text-emerald-400"
                    : acct.status === "PENDING_VERIFICATION"
                      ? "bg-yellow-500/10 text-yellow-400"
                      : "bg-[#333333] text-[#999999]"
                }`}>
                  {acct.status}
                </span>
              </div>
              <div className="text-xs text-[#CCCCCC] mb-1">{acct.accountLabel}</div>
              <div className="text-xs text-[#8A8A8A]">{acct.currency.code}</div>
              <div className="text-[11px] text-[#666666] font-mono truncate mt-2">{acct.id}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Platform Transactions */}
      <div className="bg-[#111111] border border-[#222222] rounded-lg overflow-hidden">
        <div className="p-5 border-b border-[#222222]">
          <h2 className="text-sm font-semibold text-[#F8F8F7]">Recent Platform Transactions</h2>
        </div>
        {getDemoTransactions("PLATFORM").length === 0 ? (
          <div className="p-8 text-center text-sm text-[#666666]">
            No platform transactions yet. Use the transfer form above to move funds.
          </div>
        ) : (
          <div className="divide-y divide-[#222222]">
            {getDemoTransactions("PLATFORM")
              .slice(0, 10)
              .map((tx) => (
                <div key={tx.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        tx.type === "INCOMING"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-[#00B3E0]/10 text-[#00B3E0]"
                      }`}
                    >
                      {tx.type === "INCOMING" ? "IN" : "OUT"}
                    </div>
                    <div>
                      <div className="text-sm text-[#F8F8F7]">{tx.description}</div>
                      <div className="text-xs text-[#666666]">
                        {new Date(tx.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono text-[#F8F8F7]">
                      {formatCurrencyAmount(tx.type === "INCOMING" ? tx.receivedAmount : tx.sentAmount)}
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        tx.status === "COMPLETED"
                          ? "bg-emerald-500/10 text-emerald-400"
                          : "bg-yellow-500/10 text-yellow-400"
                      }`}
                    >
                      {tx.status}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
